import { Vehicle } from "../types/models";
import { HttpClient } from "./http-client";

export class VehicleService {
  private readonly http: HttpClient;

  constructor(apiUrl: string) {
    this.http = new HttpClient(apiUrl);
  }

  async getAllVehicles(): Promise<Vehicle[]> {
    return this.http.get<Vehicle[]>("/api/gestionnaire/vehicules");
  }

  /**
   * Récupère un véhicule par son id.
   *
   * Le back n'expose pas encore de `GET /vehicules/{id}` : on filtre donc la
   * liste complète ici plutôt que dans les pages, pour n'avoir qu'un seul
   * endroit à modifier le jour où l'endpoint existe.
   */
  async getVehicleById(idVehicle: number): Promise<Vehicle | undefined> {
    const vehicles = await this.getAllVehicles();

    return vehicles.find((vehicle) => vehicle.id === idVehicle);
  }

  /** Flotte d'une entreprise : les véhicules qu'on peut associer à ses chauffeurs. */
  async getCompanyVehicles(idEntreprise: number): Promise<Vehicle[]> {
    return this.http.get<Vehicle[]>(
      `/api/v2/entreprises/${idEntreprise}/vehicules`,
    );
  }

  async createVehicle(idEntreprise: number, vehicle: any): Promise<void> {
    await this.http.post(`/api/entreprises/${idEntreprise}/vehicule`, vehicle);
  }

  async updateVehicle(
    idEntreprise: number,
    idVehicle: number,
    vehicle: any,
  ): Promise<void> {
    await this.http.put(
      `/api/entreprises/${idEntreprise}/vehicule/${idVehicle}`,
      vehicle,
    );
  }

  async deleteVehicle(idEntreprise: number, idVehicle: number): Promise<void> {
    await this.http.delete(
      `/api/entreprises/${idEntreprise}/vehicule/${idVehicle}`,
    );
  }

  async addDocumentVehicle(
    entrepriseId: number,
    idVehicle: number,
    documentFormData: FormData,
  ): Promise<void> {
    await this.http.post(
      `/api/entreprises/${entrepriseId}/vehicule/${idVehicle}/document`,
      documentFormData,
    );
  }

  async loadDocumentFile(
    idVehicle: number,
    entrepriseId: number,
    documentId: number,
  ): Promise<Blob> {
    return this.http.getBlob(
      `/api/entreprises/${entrepriseId}/vehicule/${idVehicle}/document/${documentId}`,
    );
  }

  /**
   * Verso d'un document. Le back répond 404 si le document n'en a pas : n'appeler
   * que si `idDocumentFileVerso` est renseigné sur la fiche.
   */
  async loadDocumentVersoFile(
    idVehicle: number,
    entrepriseId: number,
    documentId: number,
  ): Promise<Blob> {
    return this.http.getBlob(
      `/api/entreprises/${entrepriseId}/vehicule/${idVehicle}/document/${documentId}/verso`,
    );
  }

  async deleteDocumentVehicle(
    idVehicle: number,
    entrepriseId: number,
    documentId: number,
  ): Promise<void> {
    await this.http.delete(
      `/api/entreprises/${entrepriseId}/vehicule/${idVehicle}/document/${documentId}`,
    );
  }

  async loadLicenceFile(
    idVehicle: number,
    entrepriseId: number,
    documentId: number,
  ): Promise<Blob> {
    return this.http.getBlob(
      `/api/entreprises/${entrepriseId}/vehicule/${idVehicle}/licence/${documentId}`,
    );
  }

  async addLicenceVehicle(
    entrepriseId: number,
    idVehicle: number,
    documentFormData: FormData,
  ): Promise<void> {
    await this.http.post(
      `/api/entreprises/${entrepriseId}/vehicule/${idVehicle}/licence`,
      documentFormData,
    );
  }

  async deleteLicenceVehicle(
    idVehicle: number,
    entrepriseId: number,
    licenceId: number,
  ): Promise<void> {
    await this.http.delete(
      `/api/entreprises/${entrepriseId}/vehicule/${idVehicle}/licence/${licenceId}`,
    );
  }
}
